import type { ChatMention } from '../shared/chatMentions';

export interface ComposerDraft {
  text: string;
  chatMentions: ChatMention[];
  images: File[];
  document: File | null;
}

export const emptyComposerDraft = (): ComposerDraft => ({ text: '', chatMentions: [], images: [], document: null });

export interface ComposerDraftBinding {
  readonly key: string;
  read: () => ComposerDraft;
  write: (draft: ComposerDraft) => void;
  activate: () => () => void;
  isActive: () => boolean;
}

// Owned by App: files and text live only until this frontend session or UID ends.
// Preview URLs belong to the mounted composer and are never retained here.
export function createComposerDraftStore() {
  let owner: string | null = null;
  let session = 0;
  let nextBinding = 0;
  let active: ComposerDraftBinding | null = null;
  const entries = new Map<string, { chatId: string | null; binding: ComposerDraftBinding; draft: ComposerDraft }>();

  const deactivate = () => { active = null; };
  return {
    setUser(uid: string | null) {
      if (owner === uid) return;
      owner = uid;
      session += 1;
      entries.clear();
      deactivate();
    },
    deactivate,
    clearIfUnchanged(uid: string, binding: ComposerDraftBinding, submitted: ComposerDraft) {
      if (owner !== uid) return false;
      const entry = [...entries.values()].find(candidate => candidate.binding === binding);
      if (!entry || entry.draft !== submitted) return false;
      entry.draft = emptyComposerDraft();
      return true;
    },
    bind(uid: string, chatId: string | null, newDraftId: number): ComposerDraftBinding {
      const id = JSON.stringify(chatId === null ? ['new', newDraftId] : ['chat', chatId]);
      const previous = entries.get(id);
      if (previous) return previous.binding;
      const boundSession = session;
      const valid = () => owner === uid && session === boundSession && entries.get(id)?.binding === binding;
      const binding: ComposerDraftBinding = {
        key: JSON.stringify([uid, boundSession, ++nextBinding, id]),
        read: () => valid() ? entry.draft : emptyComposerDraft(),
        write(next) { if (binding.isActive()) entry.draft = next; },
        isActive: () => valid() && active === binding,
        activate() {
          if (valid()) active = binding;
          return () => { if (active === binding) deactivate(); };
        }
      };
      const entry = { chatId, binding, draft: emptyComposerDraft() };
      if (owner === uid) entries.set(id, entry);
      return binding;
    },
    promoteToChat(uid: string, source: ComposerDraftBinding, chatId: string) {
      if (owner !== uid) return false;
      const origin = [...entries].find(([, entry]) => entry.binding === source && entry.chatId === null);
      if (!origin) return false;
      const shouldSelect = source.isActive();
      const destination = this.bind(uid, chatId, 0);
      entries.get(JSON.stringify(['chat', chatId]))!.draft = origin[1].draft;
      entries.delete(origin[0]);
      if (shouldSelect) destination.activate();
      return shouldSelect;
    },
    removeChats(uid: string, chatIds: string[]) {
      if (owner !== uid) return;
      const deleted = new Set(chatIds);
      for (const [key, entry] of entries) {
        if (entry.chatId !== null && deleted.has(entry.chatId)) {
          if (active === entry.binding) deactivate();
          entries.delete(key);
        }
      }
    }
  };
}
